/**
 * Utility functions for handling, validating, and formatting dates
 * throughout the application.
 */

import { durationToSeconds } from './timeConstants.js'

// Single utility that returns server time (replaces all browser `new Date()`)
const getCurrentTime = async () => {
  // In cloud development, fall back to browser time since server-time endpoint doesn't exist
  if (isCloudDevelopment()) {
    return new Date()
  }
  
  const response = await fetch('/api/server-time')
  const { serverTime } = await response.json()
  return new Date(serverTime)
}

// Detect if we're running in cloud development mode
const isCloudDevelopment = () => {
  // Check multiple indicators for cloud environment
  if (typeof window === 'undefined') return false
  
  // Check for Vite development environment (connect app uses Vite)
  if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) return true
  
  // Check for localhost:3001 (connect app port)
  if (window.location.hostname === 'localhost' && window.location.port === '3001') return true
  
  // Check for Firebase hosting environment
  if (window.location.hostname.includes('firebaseapp.com') || window.location.hostname.includes('web.app')) return true
  
  // Check for production connect domain
  if (window.location.hostname === 'connect.revatek.com') return true
  
  // Check for connect-specific paths or elements
  if (window.location.pathname.includes('/connect')) return true
  
  // Check for Firebase/cloud-specific global objects
  if (typeof window.firebase !== 'undefined' || typeof window.__FIREBASE_DEFAULTS__ !== 'undefined') return true
  
  return false
}

/**
 * Fetches the earliest date available in the database
 * @returns {Promise<Date>} Promise that resolves to the earliest date
 * @throws {Error} If the fetch fails or returns invalid data
 */
export const fetchEarliestDate = async () => {
  try {
    // In cloud development, return a reasonable default date to prevent errors
    // The cloud version will use InfluxDB via Cloud Functions
    if (isCloudDevelopment()) {
      // Return a date 30 days ago as a reasonable default
      const defaultDate = new Date()
      defaultDate.setDate(defaultDate.getDate() - 30)
      return defaultDate
    }
    
    const response = await fetch('/api/earliest-date')
    const { earliestDate } = await response.json()
    const earliest = new Date(earliestDate)
    if (isNaN(earliest)) throw new Error('Invalid date received from API')
    return earliest
  } catch (error) {
    console.error('Error fetching earliest date:', error)
    throw new Error(`Failed to fetch earliest date: ${error.message}`)
  }
}

/**
 * Creates a time range object for "All time" using the earliest date
 * @param {Date} earliestDate - The earliest date from the database
 * @returns {Object} Time range object with startTimeISO, endTimeISO and duration
 */
export const createAllTimeRange = async (earliestDate) => {
  if (!earliestDate || isNaN(earliestDate)) throw new Error('Invalid earliest date')
  const endTime = await getCurrentTime()  // CHANGE: new Date() → getCurrentTime()
  return {
    startTimeISO: earliestDate.toISOString(),
    endTimeISO: endTime.toISOString(),
    duration: 'allTime',
  }
}

/**
 * Creates a time range object for a specific duration
 * @param {number} durationInSeconds - Duration in seconds 
 * @returns {Object} Time range object with startTime, endTime and duration
 */
export const createTimeRange = async (durationInSeconds) => {
  const endTime = await getCurrentTime()  // CHANGE: new Date() → getCurrentTime()
  const startTime = new Date(endTime - durationInSeconds * 1000)
  return { startTime, endTime, duration: durationInSeconds }
}

/**
 * Parses and validates a value to create a proper Date object.
 * It handles Date instances (cloning them), strings, and numeric strings representing timestamps.
 * @param {Date|string|number} value - The value to parse and validate.
 * @returns {Date} - A valid Date object.
 * @throws {RangeError} If the value cannot be converted to a valid date.
 */
export const parseAndValidateDate = (value) => {
  if (value instanceof Date) {
    // Clone Date objects to prevent unintended mutations
    const clonedDate = new Date(value.getTime())
    if (isNaN(clonedDate.getTime())) {
      throw new RangeError('Invalid Date instance provided.')
    }
    return clonedDate
  }

  if (typeof value === 'string') {
    // Handle numeric strings that might be timestamps
    if (/^\d+$/.test(value)) {
      const numDate = new Date(parseInt(value, 10))
      if (!isNaN(numDate.getTime())) {
        return numDate
      }
    }
    // Try to parse other string formats
    const dateFromString = new Date(value)
    if (!isNaN(dateFromString.getTime())) {
      return dateFromString
    }
  }

  // Try to parse numbers (could be timestamps)
  if (typeof value === 'number') {
    const dateFromNumber = new Date(value)
    if (!isNaN(dateFromNumber.getTime())) {
      return dateFromNumber
    }
  }

  throw new RangeError(`Invalid date value: Could not parse '${value}' as a valid date.`)
}

/**
 * Validates a timeRange object and ensures it has startTime and endTime as Date objects.
 * The input timeRange can have startTime/endTime as Date objects or ISO strings.
 * @param {Object} timeRange - The time range object.
 *   Expected to have (startTimeISO and endTimeISO) or (startTime and endTime).
 * @returns {Object} A new object based on timeRange, guaranteed to have startTime and endTime as valid Date objects.
 *   Preserves other properties from the original timeRange.
 * @throws {RangeError} If the timeRange object is invalid or dates cannot be validated.
 */
export const validateTimeRange = (timeRange) => {
  try {
    if (!timeRange || typeof timeRange !== 'object') {
      throw new RangeError('Invalid timeRange: input must be an object.')
    }

    let startTime
    let endTime

    // Prioritize ISO string properties if they exist
    if (timeRange.startTimeISO !== undefined && timeRange.endTimeISO !== undefined) {
      startTime = parseAndValidateDate(timeRange.startTimeISO)
      endTime = parseAndValidateDate(timeRange.endTimeISO)
    } 
    // Otherwise, use startTime/endTime properties
    else if (timeRange.startTime !== undefined && timeRange.endTime !== undefined) {
      startTime = parseAndValidateDate(timeRange.startTime)
      endTime = parseAndValidateDate(timeRange.endTime)
    } 
    // If neither complete pair of dates is found, the timeRange is considered incomplete
    else {
      throw new RangeError('Incomplete timeRange: must include either (startTimeISO and endTimeISO) or (startTime and endTime).')
    }

    // Return a new object spreading the original timeRange,
    // ensuring startTime and endTime are the validated Date objects.
    return {
      ...timeRange,
      startTime,
      endTime,
    }

  } catch (error) {
    // Wrap any error with additional context, maintaining the original error type if it's already RangeError
    if (error instanceof RangeError) {
        throw new RangeError(`Time range validation failed: ${error.message}`)
    }
    // For other error types caught from validateDate (though it primarily throws RangeError)
    throw new Error(`Time range validation failed unexpectedly: ${error.message}`)
  }
}

/**
 * Format a Date object for use in a datetime-local input
 * @param {Date} date - The date to format
 * @returns {string} Formatted date string for HTML datetime-local input
 */
export const formatDateForInput = (date) => {
  if (!date || isNaN(date)) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

/**
 * Returns a default custom date range (e.g., yesterday).
 * @returns {{startDate: Date, endDate: Date}} An object with startDate and endDate Date objects.
 */
export const getDefaultCustomDateRange = () => {
  const endDate = new Date() // Now
  const startDate = new Date()
  startDate.setDate(endDate.getDate() - 1) // Yesterday
  // Optional: Set specific times, e.g., start of yesterday to end of yesterday
  // startDate.setHours(0, 0, 0, 0);
  // endDate.setHours(23, 59, 59, 999); // Or set endDate to now, depending on desired default
  return { startDate, endDate }
}

/**
 * Normalizes a timeRange parameter to a standard format
 * @param {Object|string} timeRange - Duration string or timeRange object
 * @returns {Object|null} Normalized timeRange object or null if invalid
 */
export const normalizeTimeRange = async (timeRange) => {
  if (!timeRange) return null
  
  // If it's already a proper timeRange object, return as-is
  if ((timeRange.startTime && timeRange.endTime) || (timeRange.startTimeISO && timeRange.endTimeISO)) {
    return timeRange
  }
  
  // Extract duration string from object or use directly
  const duration = timeRange.duration || timeRange
  if (typeof duration !== 'string') return null
  
  // Handle 'allTime' - use Unix epoch to capture ALL historical data
  if (duration === 'allTime') {
    const secondsSinceEpoch = Math.floor(Date.now() / 1000)
    return await createTimeRange(secondsSinceEpoch)
  }
  
  // Handle standard durations using shared constants
  const durationSeconds = durationToSeconds(duration)
  return durationSeconds ? await createTimeRange(durationSeconds) : null
}